import { useEffect, useMemo, useState } from "react";
import { HouseholdUnlock } from "../../app/components/HouseholdUnlock";
import { InvalidAddress } from "../../app/components/InvalidAddress";
import {
  PortfolioBootstrap,
  requestPortfolioPageState,
  type PortfolioPageStateLoad,
} from "../../app/components/PortfolioBootstrap";
import { LoadingState } from "../../app/components/ui/LoadingState";
import { parseTimeRangeSearch, presetTimeRange } from "../../lib/time-range";
import { isWorkspaceRouteValid } from "../../lib/workspace-navigation";
import { apiBaseUrl, apiHref } from "./api-runtime";
import { RuntimeActivity } from "./RuntimeActivity";
import { markWorkspacePerformance, workspacePerformanceMarks } from "./workspace-performance";

type BootstrapState = {
  state: "denied" | "setup" | "locked" | "ready";
  user: { email: string; displayName: string; provider: string };
  passwordConfigured: boolean;
};

const ACCESS_BRIDGE_ATTEMPT_KEY = "qy_access_bridge_attempted_at";
const ACCESS_BRIDGE_RETRY_WINDOW_MS = 5 * 60_000;

function navigateToAccessBridge() {
  const returnTo = `${window.location.pathname}${window.location.search}${window.location.hash}`;
  window.sessionStorage.setItem(ACCESS_BRIDGE_ATTEMPT_KEY, String(Date.now()));
  window.location.replace(apiHref(`/api/auth/bridge?return_to=${encodeURIComponent(returnTo)}`));
}

function recentlyAttemptedAccessBridge() {
  const attemptedAt = Number(window.sessionStorage.getItem(ACCESS_BRIDGE_ATTEMPT_KEY) || 0);
  return Number.isFinite(attemptedAt) && Date.now() - attemptedAt < ACCESS_BRIDGE_RETRY_WINDOW_MS;
}

function renewAccessOrThrow(message: string) {
  if (recentlyAttemptedAccessBridge()) throw new Error(message);
  navigateToAccessBridge();
}

export function App() {
  const [bootstrap, setBootstrap] = useState<BootstrapState | null>(null);
  const [error, setError] = useState("");
  const [initialStateRequest, setInitialStateRequest] = useState<Promise<PortfolioPageStateLoad> | null>(null);

  const route = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    const view = params.get("view") ?? undefined;
    const initialTimeRange = parseTimeRangeSearch(params, presetTimeRange("1y"));
    return {
      view,
      section: params.get("section") ?? undefined,
      period: params.get("period") ?? undefined,
      range: (view ?? "overview") === "overview"
        ? parseTimeRangeSearch(params, presetTimeRange("ytd"))
        : initialTimeRange,
      valid: isWorkspaceRouteValid(view, params.get("section")),
    };
  }, []);

  useEffect(() => {
    markWorkspacePerformance(workspacePerformanceMarks.navigationStart);
    const controller = new AbortController();
    const pageStateRequest = route.valid
      ? requestPortfolioPageState({
        initialView: route.view,
        initialSection: route.section,
        initialTimeRange: route.range,
      }, controller.signal)
      : null;
    queueMicrotask(() => {
      if (!controller.signal.aborted) setInitialStateRequest(pageStateRequest);
    });
    async function load() {
      try {
        const response = await fetch("/api/bootstrap", { cache: "no-store", signal: controller.signal });
        if (response.status === 401 || response.status === 403) {
          renewAccessOrThrow("Google 身份自动续期未生效，请重新连接并完成登录");
          return;
        }
        const contentType = response.headers.get("content-type") || "";
        if (response.redirected || !contentType.toLowerCase().includes("application/json")) {
          renewAccessOrThrow("身份服务返回了登录页面，自动续期尚未完成");
          return;
        }
        const payload = await response.json() as BootstrapState & { detail?: string };
        if (!response.ok) throw new Error(payload.detail || `启动请求失败（HTTP ${response.status}）`);
        window.sessionStorage.removeItem(ACCESS_BRIDGE_ATTEMPT_KEY);
        setBootstrap(payload);
      } catch (loadError) {
        if (controller.signal.aborted) return;
        if (apiBaseUrl && !recentlyAttemptedAccessBridge()) {
          navigateToAccessBridge();
          return;
        }
        setError(loadError instanceof Error ? loadError.message : "无法连接家庭资产服务");
      }
    }
    void load();
    return () => controller.abort();
  }, [route]);

  useEffect(() => {
    if (bootstrap?.state === "ready" && route.valid) {
      markWorkspacePerformance(workspacePerformanceMarks.shellReady);
    }
  }, [bootstrap?.state, route.valid]);

  if (error) return <main className="unlock-shell"><section className="unlock-card signin-card">
    <div className="unlock-brand"><span className="brand-mark" aria-hidden="true" /><div><strong>权宇宙</strong><small>家庭资产星图</small></div></div>
    <div className="unlock-copy"><span className="eyebrow">API unavailable</span><h1>家庭资产服务暂时无法连接</h1><p>{error}</p></div>
    <button className="signin-button" type="button" onClick={() => {
      window.sessionStorage.removeItem(ACCESS_BRIDGE_ATTEMPT_KEY);
      navigateToAccessBridge();
    }}>重新连接</button>
  </section></main>;

  if (!bootstrap) return <main className="ledger-route-loading"><LoadingState label="正在连接家庭资产服务" detail="验证 Google 身份与后端运行状态" /></main>;

  if (bootstrap.state === "denied") return <main className="unlock-shell"><section className="unlock-card signin-card">
    <div className="unlock-brand"><span className="brand-mark" aria-hidden="true" /><div><strong>权宇宙</strong><small>家庭资产星图</small></div></div>
    <div className="unlock-copy"><span className="eyebrow">Access denied</span><h1>当前账号未获家庭授权</h1><p>请由家庭管理员将 {bootstrap.user.email} 加入成员访问名单。</p></div>
    <a className="identity-signout" href={apiHref("/api/auth/logout")}>退出并更换 Google 账号</a>
  </section></main>;

  if (bootstrap.state === "setup" || bootstrap.state === "locked") {
    return <HouseholdUnlock
      email={bootstrap.user.email}
      requiresSetup={bootstrap.state === "setup"}
      logoutHref={apiHref("/api/auth/logout")}
    />;
  }

  if (!route.valid) return <InvalidAddress />;

  return <>
    <PortfolioBootstrap
      initialView={route.view}
      initialSection={route.section}
      initialTimeRange={route.range}
      initialPeriod={route.period}
      initialStateRequest={initialStateRequest}
      user={{ email: bootstrap.user.email, displayName: bootstrap.user.displayName }}
    />
    <RuntimeActivity enabled />
  </>;
}
